import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { HeaderComponent } from "../components/header/header.component";
import { SidebarComponent } from "../components/sidebar/sidebar.component";
import { SubheaderComponent } from "../components/subheader/subheader.component";
import { DropdownModule } from "primeng/dropdown";
import { InputTextModule } from "primeng/inputtext";
import { ButtonModule } from "primeng/button";
import { CustomValidators } from "../shared/validators/custom-validators";

@Component({
  selector: "app-address-personal",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HeaderComponent,
    SidebarComponent,
    SubheaderComponent,
    DropdownModule,
    InputTextModule,
    ButtonModule
  ],
  templateUrl: "./address-personal.component.html",
  styleUrl: "./address-personal.component.css",
})
export class AddressPersonalComponent implements OnInit {
  addressForm!: FormGroup;

  // Opciones para los dropdowns
  colonyOptions: any[] = [];
  municipalityOptions: any[] = [];
  
  housingTypeOptions = [
    { label: 'Casa propia', value: 'casa_propia' },
    { label: 'Casa rentada', value: 'casa_rentada' },
    { label: 'Departamento propio', value: 'depto_propio' },
    { label: 'Departamento rentado', value: 'depto_rentado' },
    { label: 'Casa familiar', value: 'casa_familiar' },
    { label: 'Otro', value: 'otro' }
  ];

  yearsOfResidenceOptions = [
    { label: 'Menos de 1 año', value: 'menos_1' },
    { label: '1-2 años', value: '1_2' },
    { label: '3-5 años', value: '3_5' },
    { label: '6-10 años', value: '6_10' },
    { label: 'Más de 10 años', value: 'mas_10' }
  ];

  constructor(
    private router: Router,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.addressForm = this.fb.group({
      street: ['', [Validators.required, Validators.minLength(5)]],
      exteriorNumber: ['', [Validators.required, Validators.minLength(1)]],
      interiorNumber: [''],
      postalCode: ['', [Validators.required, CustomValidators.postalCode]],
      colony: ['', Validators.required],
      municipality: ['', Validators.required],
      state: ['', Validators.required],
      housingType: ['', Validators.required],
      yearsOfResidence: ['', Validators.required]
    });

    // Suscribirse a cambios en el código postal
    this.addressForm.get('postalCode')?.valueChanges.subscribe(value => {
      if (value && value.length === 5) {
        this.loadColoniesAndMunicipalities(value);
      } else {
        this.colonyOptions = [];
        this.municipalityOptions = [];
        this.addressForm.patchValue({
          colony: '',
          municipality: '',
          state: ''
        });
      }
    });
  }

  // Getters para fácil acceso a los controles
  get street() { return this.addressForm.get('street'); }
  get exteriorNumber() { return this.addressForm.get('exteriorNumber'); }
  get interiorNumber() { return this.addressForm.get('interiorNumber'); }
  get postalCode() { return this.addressForm.get('postalCode'); }
  get colony() { return this.addressForm.get('colony'); }
  get municipality() { return this.addressForm.get('municipality'); }
  get state() { return this.addressForm.get('state'); }
  get housingType() { return this.addressForm.get('housingType'); }
  get yearsOfResidence() { return this.addressForm.get('yearsOfResidence'); }

  // Método para obtener mensaje de error
  getErrorMessage(controlName: string): string {
    const control = this.addressForm.get(controlName);
    if (!control || !control.errors || !control.touched) return '';

    const errors = control.errors;

    if (errors['required']) return 'Este campo es obligatorio';
    if (errors['invalidPostalCode']) return 'Código postal inválido (5 dígitos)';
    if (errors['minlength']) return `Mínimo ${errors['minlength'].requiredLength} caracteres`;

    return 'Campo inválido';
  }

  // Método para verificar si un campo tiene errores
  hasError(controlName: string): boolean {
    const control = this.addressForm.get(controlName);
    return !!(control && control.errors && control.touched);
  }

  // Método para cargar colonias y municipios (simulado)
  loadColoniesAndMunicipalities(postalCode: string): void {
    // Simulación de datos - en producción esto vendría de una API
    this.colonyOptions = [
      { label: 'Centro', value: 'centro' },
      { label: 'Roma Norte', value: 'roma_norte' },
      { label: 'Condesa', value: 'condesa' },
      { label: 'Polanco', value: 'polanco' }
    ];
    
    this.municipalityOptions = [
      { label: 'Cuauhtémoc', value: 'cuauhtemoc' },
      { label: 'Miguel Hidalgo', value: 'miguel_hidalgo' },
      { label: 'Benito Juárez', value: 'benito_juarez' }
    ];
    
    // Establecer estado automáticamente
    this.addressForm.patchValue({ state: 'Ciudad de México' });
  }

  // Solo permitir números
  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    return charCode >= 48 && charCode <= 57;
  }
  
  goBack(): void {
    this.router.navigate(['/personal-data']);
  }

  onSubmit(): void {
    if (this.addressForm.valid) {
      console.log('Dirección personal válida:', this.addressForm.value);
      this.router.navigate(['/business-data']);
    } else {
      this.addressForm.markAllAsTouched();
      console.log('Formulario inválido');
    }
  }
}
